import { RouteProp, useIsFocused, useRoute } from "@react-navigation/native";
import { Suspense, useCallback, useEffect } from "react";
import { StatusBar } from "react-native";
import { useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { dateUtils } from "@whoosh/utilities";

import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import {
  TeeSheetQueryRenderer,
  TeeSheetQueryRendererQueryString,
} from "./TeeSheetQueryRenderer";
import { TeeSheetQueryRendererQuery } from "./__generated__/TeeSheetQueryRendererQuery.graphql";
import { useHandleTeeSheetQueryParams } from "./hooks/useHandleTeeSheetQueryParams";

export const TeeSheetScreen = () => {
  useHandleTeeSheetQueryParams();

  const [queryRef, loadQuery] = useQueryLoader<TeeSheetQueryRendererQuery>(
    TeeSheetQueryRendererQueryString
  );

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();
  const isFocused = useIsFocused();

  const todaysDate = dateUtils.useTodaysDate();
  const dateParam = route?.params?.date ?? "today";
  const date = dateUtils.isDateStringValid(dateParam) ? dateParam : todaysDate;

  const fetchSheet = useCallback(() => {
    if (loadQuery) {
      loadQuery(
        {
          date,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [loadQuery, date]);

  useEffect(() => {
    if (isFocused) {
      fetchSheet();
    }
  }, [fetchSheet, isFocused]);

  return (
    <Box flex={1} safeAreaTop backgroundColor="surface-primary">
      <WhiteBoxToCoverHeaderShadow />
      <StatusBar
        animated={true}
        barStyle="dark-content"
        backgroundColor="white"
      />
      <Suspense fallback={<Loader />}>
        {queryRef ? (
          <TeeSheetQueryRenderer
            {...{ queryRef }}
            selectedDate={date}
            refetchCallback={fetchSheet}
          />
        ) : null}
      </Suspense>
    </Box>
  );
};

// This is used to cover up the shadow of the header that appears in the status bar.
const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ marginTop: -60, zIndex: 1 }}
      bg="surface-primary"
    ></Box>
  );
};
